import React, {useState} from 'react';
import {Icons} from '../../../assets';
import {useOpportunity} from '../../../contexts/opportunity.provider';
import {Community} from '../../../models/community';
import {Opportunity, OpportunityStatus, OpportunityVisibility} from '../../../models/opportunity';
import {CommunityBadgesRow} from '../../../components/community-badges-row';
import {ConfirmationModal, ConfirmationVariant} from '../../../components/modals';
import {Dropdown} from '../../../components/dropdown';
import {ExpandableText} from '../../../components/expandable-text';
import {Heading, HeadingColor, HeadingLevel, HeadingVariant, HeadingWeight} from '../../../components/typography/heading';
import {Tag, TagSize, TagVariant} from '../../../components/tag';
import {Text, TextColor, TextVariant} from '../../../components/typography/text';
import {TimeStamp} from '../../../components/timestamp';

interface OpportunityDetailsSidebarProps {
  opportunity: Opportunity;
  restrictedToCommunities?: Community[];
}

export const OpportunityDetailsSidebar: React.FC<OpportunityDetailsSidebarProps> = ({opportunity, restrictedToCommunities = []}) => {
  const {deleteOpportunity} = useOpportunity();
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const dropdownItems = [
    {
      label: isDeleting ? 'Closing opportunity...' : 'Close opportunity',
      onClick: () => setShowDeleteModal(true),
      icon: <img src={Icons.XCIRCLE} alt="Close opportunity" className="w-4 h-4" />,
      className: 'text-red-600 hover:bg-red-50',
    },
  ];

  const handleConfirmDelete = async () => {
    try {
      setIsDeleting(true);
      await deleteOpportunity(opportunity.id);
      setShowDeleteModal(false);
      window.location.reload();
    } catch (error) {
      console.error('Failed to close opportunity:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCloseDeleteModal = () => {
    if (!isDeleting) setShowDeleteModal(false);
  };

  return (
    <div className="w-[480px] border border-gray-100 rounded-xl bg-gray-50/80 overflow-y-auto shadow-sm">
      <div className="p-2.5">
        <div className="space-y-3">
          <div className="sticky top-0 z-20 flex items-center justify-between gap-2 pb-2 border-b border-gray-100">
            <div className="flex items-center gap-2 min-w-0 flex-wrap">
              {opportunity.status === OpportunityStatus.CLOSED && (
                <Tag variant={TagVariant.Error} size={TagSize.SM} className="uppercase tracking-wide">
                  Closed
                </Tag>
              )}
              {opportunity.visibility === OpportunityVisibility.PRIVATE && restrictedToCommunities.length > 0 && (
                <CommunityBadgesRow badgeSize={24} communities={restrictedToCommunities} maxVisible={3} />
              )}
              {opportunity.visibility === OpportunityVisibility.PUBLIC && (
                <img src={Icons.VISIBILITY_PUBLIC} alt="Public" className="shrink-0 w-6 h-6" title="Public" />
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <TimeStamp date={opportunity.publishedAt} className="text-xs text-gray-600" />
              {opportunity.status !== OpportunityStatus.CLOSED && (
                <div
                  className="rounded-lg bg-gray-100 border border-gray-200 px-2 py-1.5 hover:bg-gray-200 transition-colors"
                  aria-label="Opportunity actions">
                  <Dropdown
                    trigger={<img src={Icons.THREE_DOT} alt="More options" className="cursor-pointer w-5 h-5 text-gray-600" />}
                    items={dropdownItems}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="mt-3">
            <Heading
              level={HeadingLevel.H1}
              variant={HeadingVariant.PAGE_TITLE}
              weight={HeadingWeight.BOLD}
              color={HeadingColor.PRIMARY}
              className="text-2xl font-bold mb-3">
              {opportunity.jobTitle}
            </Heading>

            <div className="flex flex-wrap gap-2">
              <Tag variant={TagVariant.Default} size={TagSize.SM}>
                {opportunity.formattedEmploymentType}
              </Tag>
              <Tag variant={TagVariant.Default} size={TagSize.SM}>
                {opportunity.formattedExperienceLevel}
              </Tag>
              <Tag variant={TagVariant.Default} size={TagSize.SM}>
                {opportunity.location || 'Remote'}
              </Tag>
            </div>
          </div>

          <div className="space-y-2">
            {opportunity.salary && (
              <div className="flex items-center gap-3">
                <img src={Icons.CURRENCY_CIRCLE_DOLLAR} alt="Currency" className="w-5 h-5" />
                <Text variant={TextVariant.BODY} className="font-semibold text-blue-500">
                  {opportunity.salary}
                </Text>
              </div>
            )}

            <div className="flex items-center gap-3">
              <img src={Icons.CLOCK_ICON} alt="Clock" className="w-5 h-5" />
              <Text variant={TextVariant.BODY} color={TextColor.PRIMARY}>
                Last date : {opportunity.formattedApplicationDeadline}
              </Text>
            </div>
          </div>

          {opportunity.videoUrl && (
            <div className="relative rounded-lg overflow-hidden h-48">
              <video
                src={opportunity.videoUrl?.url}
                className="w-full h-full object-cover bg-black"
                controls
                preload="metadata"
                playsInline
              />
            </div>
          )}

          {opportunity.requiredSkills && opportunity.requiredSkills.length > 0 && (
            <div className="sticky top-10 z-10 bg-gray-50 py-2 -mx-3 px-3 mb-2">
              <Heading
                level={HeadingLevel.H3}
                variant={HeadingVariant.SUBTITLE}
                weight={HeadingWeight.SEMIBOLD}
                color={HeadingColor.PRIMARY}
                className="text-sm mb-2">
                Required Skills
              </Heading>
              <div className="flex flex-wrap gap-2">
                {opportunity.requiredSkills
                  .filter(skill => skill?.name)
                  .map((skill, index) => (
                    <Tag key={skill.id || index} variant={TagVariant.Primary} size={TagSize.MD}>
                      {skill.name}
                    </Tag>
                  ))}
              </div>
            </div>
          )}

          <div>
            <Heading
              level={HeadingLevel.H3}
              variant={HeadingVariant.SUBTITLE}
              weight={HeadingWeight.SEMIBOLD}
              color={HeadingColor.PRIMARY}
              className="text-sm mb-2">
              Job Description
            </Heading>
            <ExpandableText
              maxLength={200}
              className="text-gray-600 text-sm leading-relaxed"
              seeMoreText="Expand"
              seeLessText="Collapse"
              preserveWhitespace={true}>
              {opportunity.jobDescription}
            </ExpandableText>
          </div>
        </div>

        <ConfirmationModal
          isOpen={showDeleteModal}
          onClose={handleCloseDeleteModal}
          onConfirm={() => {
            handleConfirmDelete();
          }}
          title="Close Opportunity"
          message={`Are you sure you want to close "${opportunity.jobTitle}"? This action cannot be undone.`}
          confirmText="Close"
          cancelText="Cancel"
          isLoading={isDeleting}
          variant={ConfirmationVariant.DANGER}
        />
      </div>
    </div>
  );
};
